/**
 * Doomsworn — PILLAR II: RUNES. Power forever, self later.
 *
 * Older than the axis. You do not cast a rune; you embody one.
 *
 * The engine knows exactly what runes DO. The setting has no authoritative
 * account of what they ARE, and it never gets one. Ratings, Grip, Marks,
 * Attunement sacrifice, Tilt immunity and thresholds are settled. Origin,
 * ontology, whether they are conscious, whether they truly predate Law and
 * Chaos — permanently unsettled. There is no hidden true answer anywhere,
 * including here. The narrator may only ever VOICE a school of thought
 * attached to a speaker: never synthesise across them, never adjudicate
 * between them, never invent a fresh one.
 */
/** The Old Names. Ratings run 1–10 in each. */
export const RUNE_NAMES = ['Salt', 'Ash', 'Chain', 'Thaw', 'Bell', 'Root'];
export const RUNE_RATING_MAX = 10;
/** Grip thresholds at which the bearer takes a Mark — physical, behavioural, permanent. */
export const GRIP_MARK_THRESHOLDS = [3, 6, 9];
/** At this Grip the rune acts without you. At the ceiling you become a local law of nature. */
export const GRIP_AUTONOMY_THRESHOLD = 8;
export const GRIP_CEILING = 10;
/** Attunement permanently sacrificed per +1 of rating. The arithmetic is public. */
export const ATTUNEMENT_COST_PER_RATING = 1;
/**
 * Attunement is the ceiling on rune ratings: you can only carry as much rune
 * as there is of you. Half the Attunement score, capped at 10.
 */
export function runeRatingCeiling(attunementScore) {
    return Math.max(0, Math.min(RUNE_RATING_MAX, Math.floor(attunementScore / 2)));
}
/** Raising a rating costs permanently sacrificed Attunement. */
export function attunementCostToRaise(fromRating, toRating) {
    return Math.max(0, toRating - fromRating) * ATTUNEMENT_COST_PER_RATING;
}
/**
 * Acting through a rune: `d20 + rune rating vs DC`. Runes are immune to the
 * Tilt — always — which is why the suppressed cults are the only people whose
 * power still works as the Balance thins.
 */
export function resolveRuneCheck(input) {
    const rng = input.rng ?? Math.random;
    const natural = Math.floor(rng() * 20) + 1;
    const total = natural + input.holding.rating;
    const success = total >= input.dc;
    const gripDelta = success ? 1 : 0;
    const newGrip = Math.min(GRIP_CEILING, input.holding.grip + gripDelta);
    const takesMark = success && GRIP_MARK_THRESHOLDS.includes(newGrip);
    return {
        success,
        natural,
        total,
        dc: input.dc,
        gripDelta,
        takesMark,
        autonomous: newGrip >= GRIP_AUTONOMY_THRESHOLD,
        becomesLaw: newGrip >= GRIP_CEILING,
        description: success
            ? `${input.holding.rune} answers (${total} vs DC ${input.dc}). Grip +1 — it is that much more of you now.`
            : `${input.holding.rune} does not move (${total} vs DC ${input.dc}).`,
    };
}
/** Apply a resolved check to the holding. Pure; caller persists. */
export function applyRuneCheck(holding, result) {
    return {
        ...holding,
        grip: Math.min(GRIP_CEILING, holding.grip + result.gripDelta),
    };
}
/**
 * Schools of thought on what runes ARE. Engine-held data attached to a
 * speaker. They conflict, and they never resolve. No NPC ever delivers the
 * answer — even ancient entities get to be confident and wrong.
 */
export const RUNE_SCHOOLS = [
    {
        speaker: 'The Deadhouse clerks',
        claim: 'Pre-imperial clauses that were never incorporated into the treaty.',
        interest: 'Legally convenient: it makes runes a filing problem rather than a rival cosmology.',
    },
    {
        speaker: 'The Salt cult',
        claim: 'Salt was here before the gods learned to bargain.',
        interest: 'A recruitment claim. Priority is authority.',
    },
    {
        speaker: 'A Chaos Power',
        claim: 'Scar tissue.',
        interest: 'It regards anything that will not change as an insult.',
    },
    {
        speaker: 'A rune master',
        claim: 'Laughs at all three, and does not offer a fourth.',
        interest: 'Understands more, and is progressively less able to say it in human terms.',
    },
];
//# sourceMappingURL=runes.js.map